import {
  GridColumnVisibilityModel,
  GridLogicOperator,
  type GridFilterItem,
  type GridFilterModel,
  type GridPaginationModel,
  type GridSortModel,
} from '@mui/x-data-grid-premium'
import moment from 'moment-timezone'
import PlainDate from 'lib/PlainDate'
import { type LeadTableDateFilterValue } from './filters/LeadTableDateFilter'
import { type LeadTableLookupFilterValue } from './filters/LeadTableLookupFilter'
import { loadPageSize } from './leadTablePreferences'

const DEFAULT_TIMEZONE = 'America/New_York'

export type ExternalDateFilterField = 'createdAt' | 'statusChangedAt'
export type ExternalLookupFilterField =
  | 'clientId'
  | 'clientCampaignId'
  | 'degreeProgramId'
  | 'status'
  | 'billable'
  | 'vendorId'
  | 'disposition'
  | 'secondaryTcpa'

export type ExternalFilters = Partial<
  Record<ExternalDateFilterField, LeadTableDateFilterValue> &
    Record<ExternalLookupFilterField, LeadTableLookupFilterValue> & {
      test: boolean
    }
>

type BackendPrimitiveType = 'string' | 'number' | 'boolean' | 'datetime'

type BackendColumnConfig = {
  columnName: string
  sortName?: string
  primitiveType: BackendPrimitiveType
}

type BackendBaseFilter = {
  column: string
  operator: string
  value: unknown[] | null
}

type BackendCompositeFilter = {
  and?: BackendFilter[]
  or?: BackendFilter[]
}

export type BackendFilter = BackendBaseFilter | BackendCompositeFilter

export type BackendSortDirection = 'asc' | 'desc'
export type BackendSort = Record<string, BackendSortDirection>

export function dashedToUnderscored(value: string) {
  return value.replace(/-/g, '_')
}

export function underscoredToDashed(value: string) {
  return value.replaceAll('_', '-')
}

function isCustomAttributeField(field: string) {
  return field.startsWith('clientCampaignCustomAttributes:')
}

function getCustomAttributeName(field: string): string {
  return field.replace(/^clientCampaignCustomAttributes:/, '')
}

function getBackendColumnConfig(field: string): BackendColumnConfig {
  const fieldMap: Record<string, BackendColumnConfig> = {
    id: { columnName: 'leads.id', primitiveType: 'string' },
    createdAt: { columnName: 'leads.created-at', primitiveType: 'datetime' },
    clientId: { columnName: 'clients.id', sortName: 'clients.name', primitiveType: 'number' },
    clientCampaignId: { columnName: 'client-campaigns.name', sortName: 'client-campaigns.name', primitiveType: 'string' },
    degreeProgramId: {
      columnName: 'degree-programs.id',
      sortName: 'degree-programs.name',
      primitiveType: 'number',
    },
    subId: { columnName: 'leads.subid', primitiveType: 'string' },
    source: { columnName: 'leads.source', primitiveType: 'string' },
    firstName: { columnName: 'leads.first-name', primitiveType: 'string' },
    lastName: { columnName: 'leads.last-name', primitiveType: 'string' },
    email: { columnName: 'leads.email', primitiveType: 'string' },
    phoneNumber: { columnName: 'leads.phone-number', primitiveType: 'string' },
    streetAddress: { columnName: 'leads.street-address', primitiveType: 'string' },
    city: { columnName: 'leads.city', primitiveType: 'string' },
    state: { columnName: 'leads.state', primitiveType: 'string' },
    zipCode: { columnName: 'leads.zip-code', primitiveType: 'string' },
    status: { columnName: 'leads.status', primitiveType: 'string' },
    statusCode: { columnName: 'leads.status-code', primitiveType: 'string' },
    disposition: { columnName: 'leads.disposition', primitiveType: 'string' },
    billable: { columnName: 'leads.billable', primitiveType: 'boolean' },
    rejectionReason: { columnName: 'leads.rejection-reason', primitiveType: 'string' },
    filteredReason: { columnName: 'leads.filtered-reason', primitiveType: 'string' },
    publicId: { columnName: 'leads.public-id', primitiveType: 'string' },
    test: { columnName: 'leads.test', primitiveType: 'boolean' },
    statusChangedAt: { columnName: 'leads.status-changed-at', primitiveType: 'datetime' },
    vendorId: { columnName: 'vendors.id', sortName: 'vendors.name', primitiveType: 'number' },
    externalId: { columnName: 'leads.external-id', primitiveType: 'string' },
    costInCents: { columnName: 'leads.cost-in-cents', primitiveType: 'number' },
    ipAddress: { columnName: 'leads.remote-ip', primitiveType: 'string' },
    userAgent: { columnName: 'leads.user-agent', primitiveType: 'string' },
    secondaryTcpa: { columnName: 'leads.secondary-tcpa', primitiveType: 'boolean' },
    rawRequestParameters: { columnName: 'leads.raw-params', primitiveType: 'string' },
    trackingParameters: { columnName: 'leads.tracking-params', primitiveType: 'string' },
    landingPageId: { columnName: 'landing-pages.public-id', primitiveType: 'string' },
    jornayaLeadidToken: { columnName: 'leads.jornaya-leadid-token', primitiveType: 'string' },
    complyedCreativeId: { columnName: 'leads.complyed-creative-id', primitiveType: 'number' },
  }

  if (isCustomAttributeField(field)) {
    return {
      columnName: `client-campaigns.custom-attributes:${getCustomAttributeName(field)}`,
      primitiveType: 'string',
    }
  }

  return fieldMap[field] ?? { columnName: `leads.${field}`, primitiveType: 'string' }
}

export function buildInitialPaginationModel(): GridPaginationModel {
  return {
    page: 0,
    pageSize: loadPageSize(),
  }
}

export function buildInitialSortModel(): GridSortModel {
  return [{ field: 'createdAt', sort: 'desc' }]
}

export function buildInitialFilterModel(): GridFilterModel {
  return {
    items: [],
    logicOperator: GridLogicOperator.And,
  }
}

export function buildInitialColumnVisibilityModel(
  clientCampaignCustomAttributes: string[] = []
): GridColumnVisibilityModel {
  return {
    costInCents: false,
    filteredReason: false,
    externalId: false,
    test: false,
    phoneNumber: false,
    streetAddress: false,
    city: false,
    state: false,
    zipCode: false,
    statusCode: false,
    disposition: false,
    statusChangedAt: false,
    ipAddress: false,
    userAgent: false,
    secondaryTcpa: false,
    rejectionReason: false,
    rawRequestParameters: false,
    trackingParameters: false,
    landingPageId: false,

    ...Object.fromEntries(
      clientCampaignCustomAttributes.map(attr => [
        `clientCampaignCustomAttributes:${attr}`,
        false,
      ])
    ),
  }
}

export function buildInitialExternalFilters(
  timezone: string = DEFAULT_TIMEZONE
): ExternalFilters {
  const today = PlainDate.from(moment.tz(timezone).format('YYYY-MM-DD'))

  return {
    createdAt: {
      operator: 'between',
      value: [today, today],
    },
  }
}

export function getStatusFilterValues(externalFilters: ExternalFilters): string[] {
  return externalFilters.status?.value.map(String) ?? []
}

export function toggleStatusInExternalFilters(
  externalFilters: ExternalFilters,
  status: string
): ExternalFilters {
  const currentValues = getStatusFilterValues(externalFilters)
  const nextValues = currentValues.includes(status)
    ? currentValues.filter((value) => value !== status)
    : [...currentValues, status]

  const nextFilters = { ...externalFilters }

  if (nextValues.length === 0) {
    delete nextFilters.status
    return nextFilters
  }

  nextFilters.status = {
    operator: 'list',
    value: nextValues,
  }

  return nextFilters
}

function serializeBooleanValueForBackend(value: unknown): boolean {
  if (typeof value === 'boolean') {
    return value
  }

  if (typeof value === 'string') {
    const normalizedValue = value.trim().toLowerCase()

    if (normalizedValue === 'yes' || normalizedValue === 'true') {
      return true
    }

    if (normalizedValue === 'no' || normalizedValue === 'false') {
      return false
    }
  }

  return Boolean(value)
}

function serializeNumberValueForBackend(value: unknown): number | null {
  if (typeof value === 'number' && !Number.isNaN(value)) {
    return value
  }

  if (typeof value === 'string' && value.trim() !== '') {
    const parsedValue = Number(value)

    if (!Number.isNaN(parsedValue)) {
      return parsedValue
    }
  }

  return null
}

function serializeStringValueForBackend(value: unknown): string {
  return String(value)
}

function normalizeSingleValueForPrimitiveType(
  value: unknown,
  primitiveType: BackendPrimitiveType
): string | number | boolean | null {
  if (primitiveType === 'boolean') {
    return serializeBooleanValueForBackend(value)
  }

  if (primitiveType === 'number') {
    return serializeNumberValueForBackend(value)
  }

  return serializeStringValueForBackend(value)
}

function normalizeArrayValuesForPrimitiveType(
  values: unknown[],
  primitiveType: BackendPrimitiveType
): Array<string | number | boolean> {
  if (primitiveType === 'boolean') {
    return values.map(serializeBooleanValueForBackend)
  }

  if (primitiveType === 'number') {
    return values
      .map(serializeNumberValueForBackend)
      .filter((value): value is number => value != null)
  }

  return values.map(serializeStringValueForBackend)
}

function isDateFilterValue(value: unknown): value is LeadTableDateFilterValue {
  return Boolean(
    value &&
      typeof value === 'object' &&
      'operator' in value &&
      'value' in value &&
      value.operator === 'between'
  )
}

function isLookupFilterValue(value: unknown): value is LeadTableLookupFilterValue {
  return Boolean(
    value &&
      typeof value === 'object' &&
      'operator' in value &&
      'value' in value &&
      value.operator === 'list' &&
      Array.isArray(value.value)
  )
}

function dateRangeToBackendValue(value: [PlainDate, PlainDate], timezone: string): string[] {
  const [startDate, endDate] = value

  const start = moment.tz(String(startDate), timezone).startOf('day').format()
  const end = moment.tz(String(endDate), timezone).endOf('day').format()

  return [start, end]
}

function mapMuiOperatorToBackendOperator(params: {
  operator: GridFilterItem['operator']
  primitiveType: BackendPrimitiveType
}): string | null {
  const { operator, primitiveType } = params

  switch (operator) {
    case 'contains':
      return 'contains'
    case 'doesNotContain':
      return 'not-contains'
    case 'startsWith':
      return 'starts-with'
    case 'endsWith':
      return 'ends-with'
    case 'isEmpty':
      return 'blank'
    case 'isNotEmpty':
      return 'not-blank'
    case 'isAnyOf':
      return 'list'
    case 'is':
      return 'is'
    case 'not':
      return 'not'
    case '=':
    case '==':
    case 'equals':
      return primitiveType === 'number' || primitiveType === 'boolean' ? 'equals' : 'is'
    case 'doesNotEqual':
    case '!=':
      return primitiveType === 'number' || primitiveType === 'boolean' ? 'not-equal' : 'not'
    case 'after':
    case '>':
      return 'greater-than'
    case 'onOrAfter':
    case '>=':
      return 'greater-than-or-equal'
    case 'before':
    case '<':
      return 'less-than'
    case 'onOrBefore':
    case '<=':
      return 'less-than-or-equal'
    default:
      return operator ?? null
  }
}

function buildBaseFilter(params: {
  field: string
  operator: string
  value: unknown[] | null
}): BackendBaseFilter | null {
  const { field, operator, value } = params
  const { columnName } = getBackendColumnConfig(field)

  if (
    operator !== 'blank' &&
    operator !== 'not-blank' &&
    (!value || value.length === 0)
  ) {
    return null
  }

  return {
    column: columnName,
    operator,
    value,
  }
}

function gridFilterItemToBackendFilter(item: GridFilterItem): BackendBaseFilter | null {
  if (!item.field || item.field === 'rowMenu') {
    return null
  }

  if (item.value == null) {
    if (item.operator === 'isEmpty' || item.operator === 'isNotEmpty') {
      const { primitiveType } = getBackendColumnConfig(item.field)
      const operator = mapMuiOperatorToBackendOperator({
        operator: item.operator,
        primitiveType,
      })

      if (!operator) {
        return null
      }

      return buildBaseFilter({
        field: item.field,
        operator,
        value: [],
      })
    }

    return null
  }

  const { primitiveType } = getBackendColumnConfig(item.field)
  const operator = mapMuiOperatorToBackendOperator({
    operator: item.operator,
    primitiveType,
  })

  if (!operator) {
    return null
  }

  if (operator === 'blank' || operator === 'not-blank') {
    return buildBaseFilter({
      field: item.field,
      operator,
      value: [],
    })
  }

  if (operator === 'list') {
    const rawValues = Array.isArray(item.value) ? item.value : [item.value]
    const normalizedValues = normalizeArrayValuesForPrimitiveType(rawValues, primitiveType)

    return buildBaseFilter({
      field: item.field,
      operator,
      value: normalizedValues,
    })
  }

  let rawValue = item.value

  if (item.field === 'costInCents' && typeof rawValue === 'number') {
    rawValue = Math.round(rawValue * 100)
  }

  const normalizedValue = normalizeSingleValueForPrimitiveType(rawValue, primitiveType)

  if (normalizedValue == null) {
    return null
  }

  return buildBaseFilter({
    field: item.field,
    operator,
    value: [normalizedValue],
  })
}

function externalFiltersToBackendFilters(params: {
  externalFilters: ExternalFilters
  timezone: string
}): BackendBaseFilter[] {
  const { externalFilters, timezone } = params
  const filters: BackendBaseFilter[] = []

  Object.entries(externalFilters).forEach(([field, filterValue]) => {
    if (!filterValue) {
      return
    }

    if (field === 'test') {
      const filter = buildBaseFilter({
        field,
        operator: 'equals',
        value: ['true'],
      })

      if (filter) {
        filters.push(filter)
      }

      return
    }

    if (isDateFilterValue(filterValue)) {
      const filter = buildBaseFilter({
        field,
        operator: 'between',
        value: dateRangeToBackendValue(filterValue.value, timezone),
      })

      if (filter) {
        filters.push(filter)
      }

      return
    }

    if (!isLookupFilterValue(filterValue) || filterValue.value.length === 0) {
      return
    }

    const { primitiveType } = getBackendColumnConfig(field)
    const normalizedValues = normalizeArrayValuesForPrimitiveType(
      filterValue.value,
      primitiveType
    )

    if (normalizedValues.length === 0) {
      return
    }

    const filter = buildBaseFilter({
      field,
      operator: 'list',
      value: normalizedValues,
    })

    if (filter) {
      filters.push(filter)
    }
  })

  return filters
}

export function buildBackendFilter(params: {
  filterModel: GridFilterModel
  externalFilters: ExternalFilters
  timezone: string
}): BackendFilter | null {
  const { filterModel, externalFilters, timezone } = params

  const gridFilters = filterModel.items
    .map((item) => gridFilterItemToBackendFilter(item))
    .filter(Boolean) as BackendBaseFilter[]

  const derivedExternalFilters = externalFiltersToBackendFilters({
    externalFilters,
    timezone,
  })

  const gridFilterGroup =
    gridFilters.length === 0
      ? null
      : gridFilters.length === 1
        ? gridFilters[0]
        : {
            [filterModel.logicOperator === 'or' ? 'or' : 'and']: gridFilters,
          }

  const allTopLevelFilters = [
    ...(gridFilterGroup ? [gridFilterGroup] : []),
    ...derivedExternalFilters,
  ]

  if (allTopLevelFilters.length === 0) {
    return null
  }

  if (allTopLevelFilters.length === 1) {
    return allTopLevelFilters[0]
  }

  return {
    and: allTopLevelFilters,
  }
}

export function buildBackendSort(sortModel: GridSortModel): BackendSort {
  return sortModel
    .filter((sort) => Boolean(sort.field) && Boolean(sort.sort))
    .filter((sort) => sort.sort === 'asc' || sort.sort === 'desc')
    .filter((sort) => sort.field !== 'rowMenu')
    .reduce<BackendSort>((acc, sort) => {
      const columnConfig = getBackendColumnConfig(sort.field)
      const columnName = isCustomAttributeField(sort.field)
        ? `client-campaigns.custom-attributes:${getCustomAttributeName(sort.field)}`
        : columnConfig.sortName || columnConfig.columnName

      acc[columnName] = sort.sort as BackendSortDirection
      return acc
    }, {})
}

export function buildLeadsRequestBody(params: {
  paginationModel: GridPaginationModel
  sortModel: GridSortModel
  filterModel: GridFilterModel
  externalFilters: ExternalFilters
  timezone: string
}) {
  const {
    paginationModel,
    sortModel,
    filterModel,
    externalFilters,
    timezone,
  } = params

  return {
    page: paginationModel.page + 1,
    per_page: paginationModel.pageSize,
    timezone,
    filter: buildBackendFilter({
      filterModel,
      externalFilters,
      timezone,
    }),
    order: buildBackendSort(sortModel),
  }
}
